import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ThemeProvider } from '../contexts/ThemeContext';
import { organizationApi, User, OrganizationInvite, authApi, taskApi, columnApi } from '../services/api';
import { CreateTaskData } from '../types/Task';
import Header from './Header';
import './Settings.css';

const Settings: React.FC = () => {
  const { user, organization, logout, refreshUser, refreshOrganization } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'users' | 'organization' | 'invitations' | 'import-export'>('profile');
  const [members, setMembers] = useState<User[]>([]);
  const [invites, setInvites] = useState<OrganizationInvite[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  // Invite form state
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'member' as 'member' | 'admin'
  });

  // Organization settings form state
  const [orgForm, setOrgForm] = useState({
    name: organization?.name || '',
    description: organization?.description || ''
  });

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || ''
  });

  // Email validation state
  const [emailValidation, setEmailValidation] = useState<'valid' | 'invalid' | 'neutral'>('neutral');

  // Import/Export state
  const [importProgress, setImportProgress] = useState<{
    total: number;
    processed: number;
    errors: string[];
    warnings: string[];
  } | null>(null);

  // Create new columns option
  const [createNewColumns, setCreateNewColumns] = useState(false);

  // Selected file for import
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Email validation function
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const loadInvites = useCallback(async () => {
    if (!user?.role || (user.role !== 'owner' && user.role !== 'admin')) return;
    
    try {
      const invitesData = await organizationApi.getInvites();
      setInvites(invitesData);
    } catch (error: any) {
      console.error('Failed to load invites:', error);
    }
  }, [user]);

  useEffect(() => {
    if (organization?.users) {
      setMembers(organization.users);
    }
    setOrgForm({
      name: organization?.name || '',
      description: organization?.description || ''
    });
    loadInvites();
  }, [organization, loadInvites]);

  useEffect(() => {
    if (user) {
      setProfileForm({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email
      });
      // Reset email validation when user data loads
      setEmailValidation('neutral');
    }
  }, [user]);

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      await organizationApi.inviteUser(inviteForm);
      setSuccess('Invitation sent successfully!');
      setInviteForm({ email: '', role: 'member' });
      await loadInvites();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to send invitation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to remove this user from the organization?')) {
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      await organizationApi.removeUser(userId);
      setSuccess('User removed successfully!');
      
      // Refresh organization data to update members list
      await refreshOrganization();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to remove user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveInvite = async (inviteId: number) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      await organizationApi.removeInvite(inviteId);
      setSuccess('Invitation cancelled successfully!');
      
      // Refresh invites list
      await loadInvites();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to remove invitation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      await organizationApi.update(orgForm);
      setSuccess('Organization updated successfully!');
      await refreshOrganization();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update organization');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Trim all values before sending to API
      const trimmedProfileForm = {
        first_name: profileForm.first_name.trim(),
        last_name: profileForm.last_name.trim(),
        email: profileForm.email.trim()
      };

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedProfileForm.email)) {
        setError('Please enter a valid email address');
        setIsLoading(false);
        return;
      }

      // Check if any required fields are empty after trimming
      if (!trimmedProfileForm.first_name || !trimmedProfileForm.last_name || !trimmedProfileForm.email) {
        setError('All fields are required');
        setIsLoading(false);
        return;
      }
      
      await authApi.updateProfile(trimmedProfileForm);
      setSuccess('Profile updated successfully!');
      await refreshUser();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to update profile';
      
      // Provide more specific error messages for common issues
      if (errorMessage.includes('email')) {
        if (errorMessage.includes('already taken')) {
          setError('This email address is already in use by another account');
        } else if (errorMessage.includes('validation') || errorMessage.includes('Email')) {
          setError('Please enter a valid email address');
        } else {
          setError(errorMessage);
        }
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeaveOrganization = async () => {
    if (!window.confirm('Are you sure you want to leave this organization? You will lose access to all shared tasks and data.')) {
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      await organizationApi.leave();
      setSuccess('Successfully left organization! Redirecting...');
      
      // Refresh user and organization data
      await refreshUser();
      await refreshOrganization();
      
      // Redirect after a short delay
      setTimeout(() => {
        window.location.href = '/setup-organization';
      }, 2000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to leave organization');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  const handleBackToTasks = () => {
    navigate('/app');
  };

  const canManageUsers = user?.role === 'owner' || user?.role === 'admin';
  const isOwner = user?.role === 'owner';

  // Export tasks to JSON file
  const handleExportTasks = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await taskApi.getTasks();
      const tasks = response.tasks;

      // Create export data with metadata
      const exportData = {
        version: "1.0",
        exported_at: new Date().toISOString(),
        exported_by: `${user?.first_name} ${user?.last_name}`,
        organization: organization?.name,
        total_tasks: tasks.length,
        tasks: tasks.map(task => ({
          summary: task.summary,
          description: task.description,
          tag: task.tag,
          priority: task.priority,
          labels: task.labels,
          status: task.status,
          videoTutorialTitle: task.videoTutorialTitle,
          videoTutorialLink: task.videoTutorialLink,
          owner: task.owner,
          reportedBy: task.reportedBy,
          reportedDate: task.reportedDate,
          expectedDate: task.expectedDate,
          originalEstimate: task.originalEstimate,
          actualTime: task.actualTime,
          startDate: task.startDate,
          endDate: task.endDate
        }))
      };

      // Create and download file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tasks-export-${organization?.name?.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setSuccess(`Successfully exported ${tasks.length} tasks!`);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to export tasks');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileExtension = file.name.toLowerCase().split('.').pop();
      if (fileExtension === 'csv' || fileExtension === 'json') {
        setSelectedFile(file);
        setError('');
      } else {
        setError('Unsupported file format. Please upload a CSV or JSON file.');
        setSelectedFile(null);
      }
    } else {
      setSelectedFile(null);
    }
  };

  const handleStartImport = async () => {
    if (!selectedFile) return;

    const fileExtension = selectedFile.name.toLowerCase().split('.').pop();
    
    if (fileExtension === 'csv') {
      await handleImportCSV(selectedFile);
    } else if (fileExtension === 'json') {
      await handleImportJSON(selectedFile);
    }
    
    // Keep the file selected after import for potential re-import
  };

  const handleClearSelection = () => {
    setSelectedFile(null);
    setError('');
    setImportProgress(null);
    
    // Reset file input
    const fileInput = document.getElementById('import-file') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Import CSV file with Polish headers
  const handleImportCSV = async (file: File) => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    setImportProgress(null);

    try {
      const response = await organizationApi.importCSV(file, createNewColumns);
      
      const { summary, details } = response;
      
      // Convert backend response to frontend format
      const errors: string[] = [];
      const warnings: string[] = [];
      
      details.forEach((detail: any, index: number) => {
        if (detail.errors && detail.errors.length > 0) {
          detail.errors.forEach((error: string) => {
            errors.push(`Row ${detail.row}: ${error}`);
          });
        }
        if (detail.warnings && detail.warnings.length > 0) {
          detail.warnings.forEach((warning: string) => {
            warnings.push(`Row ${detail.row}: ${warning}`);
          });
        }
      });

      setImportProgress({
        total: summary.total,
        processed: summary.total,
        errors: errors,
        warnings: warnings
      });

      if (summary.success > 0) {
        let successMessage = `Successfully imported ${summary.success} out of ${summary.total} tasks from CSV!`;
        if (summary.warnings > 0) {
          successMessage += ` ${summary.warnings} task(s) had warnings - check details below.`;
        }
        setSuccess(successMessage);
      }
      
      if (summary.errors > 0) {
        setError(`${summary.errors} tasks failed to import. Check the details below.`);
      }

    } catch (error: any) {
      setError(`Failed to import CSV: ${error.response?.data?.error || error.message}`);
      setImportProgress(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Import JSON file (existing functionality)
  const handleImportJSON = async (file: File) => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    setImportProgress(null);

    try {
      const text = await file.text();
      const importData = JSON.parse(text);

      // Validate import data structure
      if (!importData.tasks || !Array.isArray(importData.tasks)) {
        throw new Error('Invalid import file format: tasks array not found');
      }

      const tasks = importData.tasks;
      const total = tasks.length;
      let processed = 0;
      const errors: string[] = [];
      const warnings: string[] = [];

      // Valid values for validation
      const validPriorities = ['low', 'medium', 'high', 'critical'];
      const validStatuses = ['todo', 'in-progress', 'review', 'done', 'blocked'];

      // Load existing columns to check for duplicates
      let existingColumns: string[] = [];
      try {
        const columnsResponse = await columnApi.getColumns();
        existingColumns = columnsResponse.columns.map(col => col.status.toLowerCase());
        // Add existing column statuses to valid statuses
        validStatuses.push(...existingColumns.filter(status => !validStatuses.includes(status)));
      } catch (error) {
        console.warn('Could not load existing columns:', error);
      }

      setImportProgress({ total, processed: 0, errors: [], warnings: [] });

      // Import tasks one by one
      for (const taskData of tasks) {
        try {
          // Validate required fields
          if (!taskData.summary) {
            errors.push(`Task #${processed + 1}: Summary is required`);
            processed++;
            setImportProgress({ total, processed, errors: [...errors], warnings: [...warnings] });
            continue;
          }

          // Validate priority
          let priority = taskData.priority || 'medium';
          if (priority && !validPriorities.includes(priority.toLowerCase())) {
            warnings.push(`Task #${processed + 1} (${taskData.summary}): Invalid priority "${priority}", using "medium" instead`);
            priority = 'medium';
          }

          // Validate status
          let status = taskData.status || 'todo';
          let columnTitle = taskData.columnTitle || null; // New field for custom column title
          
          if (status && !validStatuses.includes(status.toLowerCase())) {
            if (createNewColumns) {
              // Try to create new column for unknown status
              try {
                const normalizedStatus = status.toLowerCase();
                
                // Check if column already exists (double-check against loaded columns)
                if (!existingColumns.includes(normalizedStatus)) {
                  // Use custom column title if provided, otherwise create from status
                  const finalColumnTitle = columnTitle || 
                    status.split('-').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') ||
                    status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
                    
                  await columnApi.createColumn({
                    title: finalColumnTitle,
                    status: normalizedStatus,
                    color: '#646669' // Default color
                  });
                  
                  // Add the new status to valid statuses and existing columns for subsequent tasks
                  validStatuses.push(normalizedStatus);
                  existingColumns.push(normalizedStatus);
                  
                  warnings.push(`Task #${processed + 1} (${taskData.summary}): Created new column "${finalColumnTitle}" for status "${status}"`);
                } else {
                  // Column already exists, just use the status
                  warnings.push(`Task #${processed + 1} (${taskData.summary}): Using existing column for status "${status}"`);
                }
                
                status = normalizedStatus;
              } catch (columnError: any) {
                // If column creation fails, fall back to todo
                warnings.push(`Task #${processed + 1} (${taskData.summary}): Failed to create column for status "${status}", using "todo" instead. Error: ${columnError.response?.data?.error || columnError.message}`);
                status = 'todo';
              }
            } else {
              warnings.push(`Task #${processed + 1} (${taskData.summary}): Invalid status "${status}", using "todo" instead`);
              status = 'todo';
            }
          }

          // Prepare task data for creation
          const createTaskData: CreateTaskData = {
            summary: taskData.summary,
            description: taskData.description || '',
            tag: taskData.tag || '', // New tags will be created automatically
            priority: priority as any,
            labels: Array.isArray(taskData.labels) ? taskData.labels : [], // New labels will be created automatically
            status: status as any,
            videoTutorialTitle: taskData.videoTutorialTitle || '',
            videoTutorialLink: taskData.videoTutorialLink || '',
            owner: taskData.owner || 'Unassigned',
            reportedBy: taskData.reportedBy || 'Import',
            expectedDate: taskData.expectedDate || '',
            originalEstimate: Number(taskData.originalEstimate) || 0
          };

          await taskApi.createTask(createTaskData);
          processed++;
          setImportProgress({ total, processed, errors: [...errors], warnings: [...warnings] });
        } catch (taskError: any) {
          const errorMsg = `Task #${processed + 1} (${taskData.summary}): ${taskError.response?.data?.error || taskError.message}`;
          errors.push(errorMsg);
          processed++;
          setImportProgress({ total, processed, errors: [...errors], warnings: [...warnings] });
        }
      }

      const successCount = processed - errors.length;
      let successMessage = '';
      
      if (successCount > 0) {
        successMessage = `Successfully imported ${successCount} out of ${total} tasks!`;
        if (warnings.length > 0) {
          successMessage += ` ${warnings.length} warning(s) occurred - check details below.`;
        }
        setSuccess(successMessage);
      }
      
      if (errors.length > 0) {
        setError(`${errors.length} tasks failed to import. Check the details below.`);
      }

      // Final update with all errors and warnings
      setImportProgress({ total, processed, errors, warnings });

    } catch (error: any) {
      setError(`Failed to import tasks: ${error.message}`);
      setImportProgress(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Download example import file
  const handleDownloadExample = () => {
    const exampleData = {
      version: "1.0",
      exported_at: new Date().toISOString(),
      exported_by: "Example User",
      organization: "Example Organization",
      total_tasks: 5,
      tasks: [
        {
          summary: "Setup development environment",
          description: "Install and configure all necessary development tools including IDE, Git, Node.js, and project dependencies. Ensure all team members have the same development setup.",
          tag: "setup",
          priority: "high",
          labels: ["backend", "frontend", "devops", "onboarding"],
          status: "todo",
          videoTutorialTitle: "Development Environment Setup Guide",
          videoTutorialLink: "https://example.com/dev-setup-tutorial",
          owner: "John Doe",
          reportedBy: "Project Manager",
          reportedDate: "2024-01-15T08:00:00.000Z",
          expectedDate: "2024-01-22T17:00:00.000Z",
          originalEstimate: 4,
          actualTime: 0,
          startDate: "",
          endDate: ""
        },
        {
          summary: "Create user authentication system",
          description: "Implement comprehensive user authentication including login, logout, registration, password reset, and user session management. Include JWT tokens and secure password hashing.",
          tag: "authentication",
          priority: "critical",
          labels: ["backend", "security", "api"],
          status: "in-progress",
          videoTutorialTitle: "Building Secure Authentication",
          videoTutorialLink: "https://example.com/auth-tutorial",
          owner: "Jane Smith",
          reportedBy: "Lead Developer",
          reportedDate: "2024-01-10T09:00:00.000Z",
          expectedDate: "2024-01-25T17:00:00.000Z",
          originalEstimate: 12,
          actualTime: 6.5,
          startDate: "2024-01-12T09:00:00.000Z",
          endDate: ""
        },
        {
          summary: "Design responsive homepage",
          description: "Create a modern, responsive homepage design that works seamlessly across all devices and screen sizes. Include hero section, feature highlights, and call-to-action buttons.",
          tag: "design",
          priority: "medium",
          labels: ["frontend", "ui", "responsive", "design"],
          status: "code-review",
          columnTitle: "Code Review", // Custom column title example
          videoTutorialTitle: "Responsive Design Best Practices",
          videoTutorialLink: "https://example.com/responsive-design",
          owner: "Alex Johnson",
          reportedBy: "UX Designer",
          reportedDate: "2024-01-05T10:00:00.000Z",
          expectedDate: "2024-01-20T17:00:00.000Z",
          originalEstimate: 8,
          actualTime: 7.5,
          startDate: "2024-01-08T09:00:00.000Z",
          endDate: ""
        },
        {
          summary: "Write unit tests for API endpoints",
          description: "Create comprehensive unit tests for all API endpoints including authentication, CRUD operations, error handling, and edge cases. Aim for 90%+ code coverage.",
          tag: "testing",
          priority: "high",
          labels: ["backend", "testing", "quality"],
          status: "ready-for-testing",
          columnTitle: "Ready for QA", // Another custom column title example
          videoTutorialTitle: "API Testing with Jest",
          videoTutorialLink: "https://example.com/api-testing",
          owner: "Mike Chen",
          reportedBy: "QA Lead",
          reportedDate: "2024-01-03T11:00:00.000Z",
          expectedDate: "2024-01-18T17:00:00.000Z",
          originalEstimate: 10,
          actualTime: 11.2,
          startDate: "2024-01-06T09:00:00.000Z",
          endDate: "2024-01-17T16:30:00.000Z"
        },
        {
          summary: "Database migration for user profiles",
          description: "Create and execute database migrations to add new user profile fields including avatar, bio, social links, and preferences. Ensure backward compatibility.",
          tag: "database",
          priority: "low",
          labels: ["backend", "database", "migration"],
          status: "blocked",
          videoTutorialTitle: "",
          videoTutorialLink: "",
          owner: "Sarah Wilson",
          reportedBy: "Backend Developer",
          reportedDate: "2024-01-14T14:00:00.000Z",
          expectedDate: "2024-01-30T17:00:00.000Z",
          originalEstimate: 3,
          actualTime: 0,
          startDate: "",
          endDate: ""
        }
      ]
    };

    const blob = new Blob([JSON.stringify(exampleData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tasks-import-example.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Clear all tasks
  const handleClearAllTasks = async () => {
    if (!window.confirm('Are you sure you want to delete ALL tasks? This action cannot be undone!')) {
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      await organizationApi.clearAllTasks();
      setSuccess('All tasks cleared successfully!');
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to clear tasks');
    } finally {
      setIsLoading(false);
    }
  };

  // Clear all columns
  const handleClearAllColumns = async () => {
    if (!window.confirm('Are you sure you want to delete ALL custom columns? This will reset columns to default and move all tasks to "todo". This action cannot be undone!')) {
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      await organizationApi.clearAllColumns();
      setSuccess('All custom columns cleared successfully!');
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to clear columns');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset all organization data
  const handleResetAllData = async () => {
    if (!window.confirm('⚠️ DANGER: This will delete ALL tasks and columns, then reset to default columns. This action cannot be undone! Are you absolutely sure?')) {
      return;
    }

    const confirmation = window.prompt('To confirm this destructive action, please type "RESET ALL DATA" exactly:');
    if (confirmation !== 'RESET ALL DATA') {
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      await organizationApi.resetAllData();
      setSuccess('Organization data reset successfully! All data has been cleared and default columns have been created.');
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to reset organization data');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ThemeProvider>
      <div className="app full-width">
        <Header onNewTask={handleBackToTasks} />
        
        <main className="main-content">
          <div className="container">
            <div className="controls-section">
              <div className="controls-section-header">
                <h2>Settings</h2>
                
              </div>
              
              <div className="controls-section-content">
                <div className="settings-content">
                  <div className="settings-tabs">
                    <button 
                      className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
                      onClick={() => setActiveTab('profile')}
                    >
                      ● profile
                    </button>
                    <button 
                      className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
                      onClick={() => setActiveTab('users')}
                    >
                      ● users ({members.length})
                    </button>
                    <button 
                      className={`tab-button ${activeTab === 'organization' ? 'active' : ''}`}
                      onClick={() => setActiveTab('organization')}
                    >
                      ● organization
                    </button>
                    {canManageUsers && (
                      <button 
                        className={`tab-button ${activeTab === 'invitations' ? 'active' : ''}`}
                        onClick={() => setActiveTab('invitations')}
                      >
                        ● invitations ({invites.length})
                      </button>
                    )}
                    {isOwner && (
                      <button 
                        className={`tab-button ${activeTab === 'import-export' ? 'active' : ''}`}
                        onClick={() => setActiveTab('import-export')}
                      >
                        ● import/export
                      </button>
                    )}
                  </div>

                  <div className="settings-panel">
                    {error && <div className="error-message">{error}</div>}
                    {success && <div className="success-message">{success}</div>}

                    {activeTab === 'profile' && (
                      <div className="profile-panel">
                        <h2>● Profile</h2>
                        <form onSubmit={handleUpdateProfile} className="profile-form">
                          <div className="form-group">
                            <label htmlFor="first-name">First Name</label>
                            <input
                              type="text"
                              id="first-name"
                              value={profileForm.first_name}
                              onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                              required
                              disabled={isLoading}
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="last-name">Last Name</label>
                            <input
                              type="text"
                              id="last-name"
                              value={profileForm.last_name}
                              onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                              required
                              disabled={isLoading}
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                              type="email"
                              id="email"
                              value={profileForm.email}
                              onChange={(e) => {
                                const newEmail = e.target.value;
                                setProfileForm({ ...profileForm, email: newEmail });
                                
                                // Real-time email validation
                                if (newEmail.trim() === '') {
                                  setEmailValidation('neutral');
                                } else if (validateEmail(newEmail)) {
                                  setEmailValidation('valid');
                                } else {
                                  setEmailValidation('invalid');
                                }
                                
                                // Clear error when user starts typing
                                if (error && error.includes('email')) {
                                  setError('');
                                }
                              }}
                              className={emailValidation === 'invalid' ? 'error' : emailValidation === 'valid' ? 'success' : ''}
                              required
                              disabled={isLoading}
                              placeholder="Enter your email address"
                            />
                          </div>
                          <button type="submit" disabled={isLoading || !profileForm.first_name.trim() || !profileForm.last_name.trim() || !profileForm.email.trim()}>
                            {isLoading ? 'Updating...' : 'Update Profile'}
                          </button>
                        </form>

                        <div className="profile-info">
                          <h3>Account Information</h3>
                          <div className="info-item">
                            <span className="info-label">Role:</span>
                            <span className={`info-value role-${user?.role}`}>
                              {user?.role === 'owner' ? (
                                <>
                                  <span className="role-icon">●</span>
                                  <span className="role-text">Owner</span>
                                </>
                              ) : user?.role === 'admin' ? (
                                <>
                                  <span className="role-icon">●</span>
                                  <span className="role-text">Admin</span>
                                </>
                              ) : (
                                <>
                                  <span className="role-icon">●</span>
                                  <span className="role-text">Member</span>
                                </>
                              )}
                            </span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Organization:</span>
                            <span className="info-value">{organization?.name || 'No organization'}</span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Member since:</span>
                            <span className="info-value">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}</span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Email verified:</span>
                            <span className={`info-value ${user?.is_email_verified ? 'verified' : 'unverified'}`}>
                              {user?.is_email_verified ? 'Verified' : 'Not verified'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === 'users' && (
                      <div className="users-panel">
                        <h2>● Organization Members ({members.length})</h2>
                        <div className="members-list">
                          {members.map((member) => (
                            <div key={member.id} className="member-item">
                              <div className="member-info">
                                <div className="member-details">
                                  <div className={`member-avatar role-${member.role}`}>
                                    {member.first_name.charAt(0).toUpperCase()}{member.last_name.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="member-name">
                                    {member.first_name} {member.last_name}
                                    {member.id === user?.id && <span className="you-badge">You</span>}
                                  </div>
                                  <div className="member-email">{member.email}</div>
                                  <div className="member-meta">
                                    <span className={`member-role role-${member.role}`}>
                                      <span className="role-text">{member.role}</span>
                                    </span>
                                    <span className={`member-status ${member.is_email_verified ? 'verified' : 'unverified'}`}>
                                      {member.is_email_verified ? 'Verified' : 'Unverified'}
                                    </span>
                                    {member.last_login_at && (
                                      <span className="member-last-seen">
                                        {new Date(member.last_login_at).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {canManageUsers && member.role !== 'owner' && member.id !== user?.id && (
                                <div className="member-actions">
                                  <button 
                                    className="remove-btn"
                                    onClick={() => handleRemoveUser(member.id)}
                                    disabled={isLoading}
                                    title={`Remove ${member.first_name} ${member.last_name} from organization`}
                                  >
                                    ×
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>

                        {canManageUsers && (
                          <div className="invite-section">
                            <h3>● Invite New Member</h3>
                            <p>Invite new team members to join your organization. They can register with the invitation link or accept it if they already have an account.</p>
                            <form onSubmit={handleInviteUser} className="invite-form">
                              <div className="form-row">
                                <div className="form-field">
                                  <label htmlFor="invite-email">Email Address</label>
                                  <input
                                    id="invite-email"
                                    type="email"
                                    placeholder="colleague@company.com"
                                    value={inviteForm.email}
                                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                                    required
                                    disabled={isLoading}
                                  />
                                </div>
                                <div className="form-field">
                                  <label htmlFor="invite-role">Role</label>
                                  <select
                                    id="invite-role"
                                    value={inviteForm.role}
                                    onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value as 'member' | 'admin' })}
                                    disabled={isLoading}
                                  >
                                    <option value="member">● Member</option>
                                    <option value="admin">● Admin</option>
                                  </select>
                                </div>
                                <div className="form-field">
                                  <button type="submit" disabled={isLoading || !inviteForm.email.trim()}>
                                    {isLoading ? 'Sending...' : 'Send Invitation'}
                                  </button>
                                </div>
                              </div>
                            </form>
                            <div className="role-explanation">
                              <h4>Role Permissions:</h4>
                              <ul>
                                <li><strong>● Member:</strong> Can view and manage tasks, but cannot invite users or modify organization settings</li>
                                <li><strong>● Admin:</strong> Can manage tasks, invite users, and modify organization settings</li>
                                <li><strong>● Owner:</strong> Full access including user management and organization deletion</li>
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === 'organization' && (
                      <div className="organization-panel">
                        <h2>● Organization Settings</h2>
                        {organization && canManageUsers && (
                          <form onSubmit={handleUpdateOrganization} className="org-settings-form">
                            <div className="form-group">
                              <label htmlFor="org-name">Organization Name</label>
                              <input
                                type="text"
                                id="org-name"
                                value={orgForm.name}
                                onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
                                required
                                disabled={isLoading}
                              />
                            </div>
                            <div className="form-group">
                              <label htmlFor="org-description">Description</label>
                              <textarea
                                id="org-description"
                                value={orgForm.description}
                                onChange={(e) => setOrgForm({ ...orgForm, description: e.target.value })}
                                disabled={isLoading}
                                rows={3}
                              />
                            </div>
                            <button type="submit" disabled={isLoading || !orgForm.name.trim()}>
                              {isLoading ? 'Updating...' : 'Update Organization'}
                            </button>
                          </form>
                        )}

                        {organization && !canManageUsers && (
                          <div className="org-info">
                            <h3>● Current Organization</h3>
                            <p><strong>Name:</strong> {organization.name}</p>
                            <p><strong>Description:</strong> {organization.description || 'No description provided'}</p>
                            <p><strong>Created:</strong> {new Date(organization.created_at).toLocaleDateString()}</p>
                            <p><strong>Members:</strong> {members.length}</p>
                          </div>
                        )}

                        {!isOwner && (
                          <div className="danger-zone">
                            <h3>Danger Zone</h3>
                            <p>
                              Leaving the organization will remove your access to all shared tasks and data. This action cannot be undone.
                            </p>
                            <button 
                              onClick={handleLeaveOrganization}
                              disabled={isLoading}
                              className="leave-org-btn"
                            >
                              {isLoading ? 'Leaving...' : 'Leave Organization'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === 'invitations' && canManageUsers && (
                      <div className="invitations-panel">
                        <h2>● Pending Invitations</h2>
                        {invites.length === 0 ? (
                          <p className="no-invites">No pending invitations</p>
                        ) : (
                          <div className="invites-list">
                            {invites.map((invite) => (
                              <div key={invite.id} className="invite-item">
                                <div className="invite-info">
                                  <div className="invite-email">{invite.email}</div>
                                  <div className={`invite-role role-${invite.role}`}>
                                    {invite.role}
                                  </div>
                                  <div className="invite-meta">
                                    Invited by {invite.invited_by.first_name} {invite.invited_by.last_name}
                                    <br />
                                    Expires: {new Date(invite.expires_at).toLocaleDateString()}
                                  </div>
                                </div>
                                <div className="invite-actions">
                                  <button 
                                    className="remove-btn"
                                    onClick={() => handleRemoveInvite(invite.id)}
                                    disabled={isLoading}
                                    title={`Remove invitation to ${invite.email}`}
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === 'import-export' && (
                      <div className="import-export-panel">
                        <h2>● Import/Export Tasks</h2>
                        
                        {!isOwner ? (
                          <div className="access-denied">
                            <h3>Access Restricted</h3>
                            <p>Import/Export functionality is only available to organization owners.</p>
                            <p>Please contact your organization owner if you need to import or export data.</p>
                          </div>
                        ) : (
                          <>
                            <div className="export-section">
                              <h3>● Export Tasks</h3>
                              <p>Download all current tasks as a JSON file for backup or transfer to another system.</p>
                              <button 
                                className="export-btn"
                                onClick={handleExportTasks} 
                                disabled={isLoading}
                              >
                                {isLoading ? 'Exporting...' : 'Export All Tasks'}
                              </button>
                            </div>

                            <div className="import-section">
                              <h3>● Import Tasks</h3>
                              <p>Import tasks from a CSV or JSON file. Tasks will be added to your current organization.</p>
                              
                              <div className="import-controls">
                                <label htmlFor="import-file" className="file-input-label">
                                  <input
                                    id="import-file"
                                    type="file"
                                    accept=".json,.csv"
                                    onChange={handleFileSelect}
                                    disabled={isLoading}
                                    className="file-input"
                                  />
                                  {selectedFile ? `Selected: ${selectedFile.name}` : 'Choose CSV or JSON File'}
                                </label>
                                
                                {selectedFile && (
                                  <div className="import-actions">
                                    <button 
                                      className="import-btn primary"
                                      onClick={handleStartImport} 
                                      disabled={isLoading}
                                    >
                                      {isLoading ? 'Importing...' : 'Import Tasks'}
                                    </button>
                                    <button 
                                      className="import-btn secondary"
                                      onClick={handleClearSelection} 
                                      disabled={isLoading}
                                    >
                                      Clear Selection
                                    </button>
                                  </div>
                                )}
                                
                                <button 
                                  className="example-btn"
                                  onClick={handleDownloadExample} 
                                  disabled={isLoading}
                                >
                                  Download Example JSON
                                </button>
                              </div>

                              <div className="import-options">
                                <label className="checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={createNewColumns}
                                    onChange={(e) => setCreateNewColumns(e.target.checked)}
                                    disabled={isLoading}
                                  />
                                  <span className="checkbox-text">
                                    Create new columns for unknown statuses
                                  </span>
                                </label>
                                <p className="option-description">
                                  When enabled, tasks with unknown status values (like "testing", "deployed") will create new columns automatically. 
                                  When disabled, they will be placed in the "todo" column with a warning.
                                </p>
                              </div>

                              {importProgress && (
                                <div className="import-progress">
                                  <h4>Import Progress</h4>
                                  <div className="progress-bar">
                                    <div 
                                      className="progress-fill" 
                                      style={{ width: `${(importProgress.processed / importProgress.total) * 100}%` }}
                                    ></div>
                                  </div>
                                  <div className="progress-text">
                                    {importProgress.processed} / {importProgress.total} tasks processed
                                  </div>
                                  
                                  {importProgress.errors.length > 0 && (
                                    <div className="import-errors">
                                      <h5>Import Errors:</h5>
                                      <ul>
                                        {importProgress.errors.slice(0, 10).map((error, index) => (
                                          <li key={`error-${index}`} className="error-item">{error}</li>
                                        ))}
                                        {importProgress.errors.length > 10 && (
                                          <li className="error-item">... and {importProgress.errors.length - 10} more errors</li>
                                        )}
                                      </ul>
                                    </div>
                                  )}

                                  {importProgress.warnings.length > 0 && (
                                    <div className="import-warnings">
                                      <h5>Import Warnings:</h5>
                                      <ul>
                                        {importProgress.warnings.slice(0, 10).map((warning, index) => (
                                          <li key={`warning-${index}`} className="warning-item">{warning}</li>
                                        ))}
                                        {importProgress.warnings.length > 10 && (
                                          <li className="warning-item">... and {importProgress.warnings.length - 10} more warnings</li>
                                        )}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}

                              <div className="import-format-info">
                                <h4>Supported File Formats:</h4>
                                
                                <div className="format-section">
                                  <h5>CSV Files (Polish Headers)</h5>
                                  <p>Upload CSV files exported from Excel or Google Sheets with Polish column headers:</p>
                                  <div className="csv-headers">
                                    <strong>Supported columns:</strong>
                                    <ul>
                                      <li><code>Summary</code> - Task title (required)</li>
                                      <li><code>Description</code> - Task description</li>
                                      <li><code>Tag</code> - Task category</li>
                                      <li><code>Priority</code> - wysoki, normalny, niski, krytyczny</li>
                                      <li><code>Labels</code> - Comma-separated labels</li>
                                      <li><code>Status</code> - any status name (creates columns automatically)</li>
                                      <li><code>Filmik Instruktarzowy</code> - Video tutorial title</li>
                                      <li><code>Link do filmiku</code> - Video tutorial link</li>
                                      <li><code>Właściciel</code> - Task owner</li>
                                      <li><code>Reported By</code> - Reporter name</li>
                                      <li><code>Reported Date</code> - Report date (YYYY-MM-DD)</li>
                                      <li><code>Expected Date</code> - Expected completion date</li>
                                      <li><code>Original estimate [h]</code> - Time estimate in hours</li>
                                      <li><code>Faktyczny czas [h]</code> - Actual time spent</li>
                                      <li><code>Data rozpoczęcia</code> - Start date</li>
                                      <li><code>Data końcowa</code> - End date</li>
                                    </ul>
                                  </div>
                                </div>

                                <div className="format-section">
                                  <h5>JSON Files</h5>
                                  <p>The import file should be a JSON file with the following structure:</p>
                                  <pre className="format-example">{`{
  "version": "1.0",
  "tasks": [
    {
      "summary": "Task title (required)",
      "description": "Task description",
      "tag": "task-category",
      "priority": "low|medium|high|critical",
      "labels": ["label1", "label2"],
      "status": "todo|in-progress|review|done|blocked",
      "columnTitle": "Custom Column Name (optional)",
      "owner": "Task assignee",
      "reportedBy": "Reporter name",
      "expectedDate": "2024-01-15T00:00:00Z",
      "originalEstimate": 4.5
    }
  ]
}`}</pre>
                                </div>
                                
                                <div className="format-rules">
                                  <h5>Import Rules:</h5>
                                  <ul>
                                    <li><strong>Required:</strong> Only <code>Summary</code> field is required</li>
                                    <li><strong>Priority:</strong> Supports both Polish (wysoki, normalny, niski, krytyczny) and English values</li>
                                    <li><strong>Status:</strong> Any status name (e.g., "Ukończono", "W trakcie", "Testowanie")
                                      <br />• Status names become column titles directly - no translation needed
                                      <br />• If "Create new columns" is <strong>enabled</strong>: New columns are created for each unique status
                                      <br />• If "Create new columns" is <strong>disabled</strong>: Unknown statuses default to "todo"
                                    </li>
                                    <li><strong>CSV Dates:</strong> Supports various formats: YYYY-MM-DD, DD-MM-YYYY, DD.MM.YYYY</li>
                                    <li><strong>CSV Numbers:</strong> Supports both dot (.) and comma (,) as decimal separator</li>
                                    <li><strong>Column Title:</strong> Optional custom name for new columns (JSON only)</li>
                                    <li><strong>Tags & Labels:</strong> New values will be created automatically</li>
                                    <li><strong>Warnings:</strong> Invalid values will show warnings but won't stop import</li>
                                  </ul>
                                </div>
                              </div>

                              {canManageUsers && (
                                <div className="clear-data-section">
                                  <h3>● Clear Data</h3>
                                  <p className="section-description">
                                    Useful for testing imports or cleaning up your organization's data. These actions cannot be undone.
                                  </p>
                                  
                                  <div className="clear-actions">
                                    <div className="clear-action-item">
                                      <div className="action-info">
                                        <h4>Clear All Tasks</h4>
                                        <p>Remove all tasks while keeping columns intact.</p>
                                      </div>
                                      <button 
                                        className="clear-btn clear-tasks-btn"
                                        onClick={handleClearAllTasks} 
                                        disabled={isLoading}
                                      >
                                        {isLoading ? 'Clearing...' : 'Clear Tasks'}
                                      </button>
                                    </div>

                                    <div className="clear-action-item">
                                      <div className="action-info">
                                        <h4>Clear Custom Columns</h4>
                                        <p>Remove all custom columns (keeps default ones: todo, in-progress, review, done, blocked).</p>
                                      </div>
                                      <button 
                                        className="clear-btn clear-columns-btn"
                                        onClick={handleClearAllColumns} 
                                        disabled={isLoading}
                                      >
                                        {isLoading ? 'Clearing...' : 'Clear Columns'}
                                      </button>
                                    </div>

                                    {isOwner && (
                                      <div className="clear-action-item danger-action">
                                        <div className="action-info">
                                          <h4>⚠️ Reset All Data</h4>
                                          <p><strong>DANGER:</strong> Remove ALL tasks and columns, then reset to default columns. Only organization owners can do this.</p>
                                        </div>
                                        <button 
                                          className="clear-btn reset-all-btn"
                                          onClick={handleResetAllData} 
                                          disabled={isLoading}
                                        >
                                          {isLoading ? 'Resetting...' : 'Reset Everything'}
                                        </button>
                                      </div>
                                    )}
                                  </div>

                                  <div className="clear-warning">
                                    <h5>⚠️ Important Notes:</h5>
                                    <ul>
                                      <li><strong>All clear operations are permanent</strong> - make sure to export your data first if you want to keep it</li>
                                      <li><strong>Clear Tasks:</strong> Removes all tasks but keeps your custom columns</li>
                                      <li><strong>Clear Columns:</strong> Removes custom columns and moves any tasks in them to "todo"</li>
                                      <li><strong>Reset All:</strong> Complete data wipe - useful for starting fresh or testing imports</li>
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </ThemeProvider>
  );
};

export default Settings; 